"use client"

import { startTransition, useEffect, useState } from "react"
import {
  BiomarkerPackageCards,
  type BiomarkerPackageCard,
  type BiomarkerAddonItem,
  type BiomarkerMembershipCard,
} from "@/components/ui/biomarker-package-cards"
import { cn } from "@/lib/utils"

interface TocItem {
  id: string
  label: string
}

interface BiomarkerSidebarProps {
  tocItems: TocItem[]
  packageCards: BiomarkerPackageCard[]
  addonPackages?: BiomarkerAddonItem[]
  membershipCard?: BiomarkerMembershipCard
  onThisPageLabel: string
  includedInLabel: string
}

export function BiomarkerSidebar({
  tocItems,
  packageCards,
  addonPackages,
  membershipCard,
  onThisPageLabel,
  includedInLabel,
}: BiomarkerSidebarProps) {
  const [activeId, setActiveId] = useState<string | null>(
    tocItems[0]?.id ?? null
  )

  useEffect(() => {
    if (tocItems.length === 0) return

    const elements = tocItems
      .map((t) => document.getElementById(t.id))
      .filter((el): el is HTMLElement => el !== null)

    if (elements.length === 0) return

    // Track intersection state for each section and pick the topmost one in view.
    // rootMargin pulls the observation band down ~120px (past sticky navbar) and
    // stops ~55% from the bottom so the active indicator flips near the top.
    const visible = new Set<string>()

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id)
          else visible.delete(entry.target.id)
        }

        if (visible.size === 0) return
        // Pick the section whose id appears first in tocItems (document order).
        const next = tocItems.find((t) => visible.has(t.id))
        if (next) startTransition(() => setActiveId(next.id))
      },
      {
        rootMargin: "-120px 0px -55% 0px",
        threshold: 0,
      }
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [tocItems])

  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string
  ) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (!el) return
    el.scrollIntoView({ behavior: "smooth", block: "start" })
    // Keep the URL in sync without a jump.
    window.history.replaceState(null, "", `#${id}`)
    setActiveId(id)
  }

  return (
    <div className="sticky top-24 flex flex-col gap-10">
      {tocItems.length > 0 && (
        <nav aria-label={onThisPageLabel}>
          <h2 className="type-label-lg mb-4 text-neutral-400">
            {onThisPageLabel}
          </h2>
          <ul className="flex flex-col gap-3">
            {tocItems.map((item) => {
              const isActive = activeId === item.id
              return (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => handleAnchorClick(e, item.id)}
                    aria-current={isActive ? "location" : undefined}
                    className={cn(
                      "type-body-md transition-colors hover:text-neutral-900",
                      isActive
                        ? "font-medium text-neutral-900"
                        : "text-neutral-500"
                    )}
                  >
                    {item.label}
                  </a>
                </li>
              )
            })}
          </ul>
        </nav>
      )}

      <BiomarkerPackageCards
        items={packageCards}
        addonItems={addonPackages}
        membershipCard={membershipCard}
        heading={includedInLabel}
      />
    </div>
  )
}
